import { MonthlyReport } from '../store/useStore'
import { PANASReport } from '../pages/Report/Step-3'
import {
  basicSelectQuestions,
  partAQuestions,
} from '../pages/Report/Step-3/utils/constants'
import { getDietStatus } from './dietStatus'

/**
 * Checks if a PANAS report is complete
 * Moved from PANAS.tsx to centralize completion logic
 */
export function isPANASReportComplete(report: PANASReport) {
  const basic = report.basic
  if (!basic) {
    return false
  }

  const requiredBasicQuestions = [...partAQuestions, ...basicSelectQuestions]
  const basicComplete = requiredBasicQuestions.every(
    ({ name }) => basic[name]?.length > 0,
  )

  return report.modifiedPanas?.complete && basicComplete
}

/**
 * Checks if all 13 steps of a monthly report are completed
 * This logic mirrors the status calculation from Home.tsx
 */
export function checkAllStepsCompleted(
  report: MonthlyReport | null | undefined,
): boolean {
  if (!report) {
    return false
  }

  // Step 1: ALSFRS-R
  const step1Complete = !!(report.alsfrs && report.alsfrs.score !== undefined)

  // Step 2: ALSSQ
  const step2Complete = !!(report.alssq && report.alssq.score !== undefined)

  // Step 3: Mindset (PANAS)
  const step3Complete = !!(report.panas && isPANASReportComplete(report.panas))

  // Step 4: Stool and Urine
  const step4Complete = !!(
    report.stool &&
    report.stool.stool.complete === true &&
    report.stool.urine.complete === true
  )

  // Step 5: Vital Statistics
  const step5Complete = !!(
    report.vitalStats && report.vitalStats.weight !== undefined
  )

  // Step 6: Journal - Clinical Observations
  const step6Complete = !!report.clinicalObservations

  // Step 7: Diet
  const dietStatus = getDietStatus(report)
  const step7Complete = dietStatus === 'complete'

  // Step 8: Supplements
  const step8Complete = !!report.supplements

  // Step 9: Medications
  const step9Complete = !!report.nonSupplementMedications

  // Step 10: Physical Strategies
  const step10Complete = !!report.physicalStrategies

  // Step 11: Mind Strategies
  const step11Complete = !!report.mentalStrategies

  // Step 12: Core Education & Steps
  const step12Complete = !!report.stages

  // Step 13: Support Team
  const step13Complete = !!report.support

  // All steps must be complete
  return (
    step1Complete &&
    step2Complete &&
    step3Complete &&
    step4Complete &&
    step5Complete &&
    step6Complete &&
    step7Complete &&
    step8Complete &&
    step9Complete &&
    step10Complete &&
    step11Complete &&
    step12Complete &&
    step13Complete
  )
}

/**
 * Returns the display names of all incomplete steps for a monthly report
 */
export function getIncompleteStepNames(
  report: MonthlyReport | null | undefined,
): string[] {
  if (!report) {
    return [
      'Step 1: ALSFRS-R',
      'Step 2: ALS Symptom Questionnaire',
      'Step 3: Mindset',
      'Step 4: Stool & Urine',
      'Step 5: Vital Statistics',
      'Step 6: Clinical Observations',
      'Step 7: Diet',
      'Step 8: Supplements',
      'Step 9: Medications',
      'Step 10: Physical Strategies',
      'Step 11: Mind Strategies',
      'Step 12: Core Education & Steps',
      'Step 13: Support Team',
    ]
  }

  const incomplete: string[] = []

  if (!(report.alsfrs && report.alsfrs.score !== undefined))
    incomplete.push('Step 1: ALSFRS-R')
  if (!(report.alssq && report.alssq.score !== undefined))
    incomplete.push('Step 2: ALS Symptom Questionnaire')
  if (!(report.panas && isPANASReportComplete(report.panas)))
    incomplete.push('Step 3: Mindset')
  if (
    !(
      report.stool &&
      report.stool.stool.complete === true &&
      report.stool.urine.complete === true
    )
  )
    incomplete.push('Step 4: Stool & Urine')
  if (!(report.vitalStats && report.vitalStats.weight !== undefined))
    incomplete.push('Step 5: Vital Statistics')
  if (!report.clinicalObservations)
    incomplete.push('Step 6: Clinical Observations')
  if (getDietStatus(report) !== 'complete') incomplete.push('Step 7: Diet')
  if (!report.supplements) incomplete.push('Step 8: Supplements')
  if (!report.nonSupplementMedications) incomplete.push('Step 9: Medications')
  if (!report.physicalStrategies)
    incomplete.push('Step 10: Physical Strategies')
  if (!report.mentalStrategies) incomplete.push('Step 11: Mind Strategies')
  if (!report.stages) incomplete.push('Step 12: Core Education & Steps')
  if (!report.support) incomplete.push('Step 13: Support Team')

  return incomplete
}
